import React, { useRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useStyles } from './MyVoicePage.styles';

import moment from 'moment';
import _ from 'lodash';
import Grid from '@material-ui/core/Grid';
import ReactTimeAgo from 'react-time-ago';

import { ButtonGroup, Button } from '@mui/material';
import DownloadIcon from '@mui/icons-material/FileDownload';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import FavoriteIcon from '@mui/icons-material/Favorite';

import DownloadModal from '../layout/DownloadModal';
import CommunityUploadModal from '../layout/CommunityUploadModal';

import ConfirmationModal from '../layout/ConfirmationModal';
import EditLabelModal from '../layout/EditLabelModal';
import EditCommunityModal from '../layout/EditCommunityModal';
import HtmlTooltip from '../HtmlTooltip';
import tooltipImg from '../../img/tooltip.png';
import {
  checkTrainingCompletion,
  checkInferenceCompletion,
  updateTrainingById,
  updateCommunityItemById,
  deleteCommunityItemById,
  updateInferenceById,
  updateVocalExtractById,
  checkVocalExtractCompletion,
  fetchInferencesChunk,
  fetchTrainingsChunk,
  fetchVocalExtractsChunk,
  fetchPagedUserCommunityItems,
  fetchUserCommunityItems,
  getCommunitySampleSignedUrl,
  updateImage,
  likeCommunityItem,
  dislikeCommunityItem,
} from '../../services/page.services';
import HtmlInfoIcon from '../HtmlInfoIcon.js';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import infoIconImg from '../../img/infoIcon.png';
import InfiniteScroll from 'react-infinite-scroll-component';
import { appActions } from '../../actions/app.actions.js';
import ReactJkMusicPlayer from 'react-jinke-music-player';
import { checkValidLoginStatus } from '../../utils/user.utils';

function MyVoicePage() {
  const { t } = useTranslation();
  const user = useSelector(state => state.user);
  const userId = user?.id;
  const dispatch = useDispatch();

  const [alignment, setAlignment] = useState('music');
  //initData
  const [data, setData] = useState([]);
  const [communityItemsLiked, setCommunityItemsLiked] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  //query
  const [query, setQuery] = useState('');
  const [communityQuery, setCommunityQuery] = useState('');

  //play
  const [selectedAudio, setSelectedAudio] = React.useState();
  const [playing, setPlaying] = useState(false);
  const [selectedCommunityId, setSelectedCommunityId] = useState('');

  //pagination
  const chunk = 50;
  const [chunkPage, setChunkPage] = useState(0);
  const [length, setLength] = useState(0);
  const controllerRef = useRef(null);
  const [paginationInfo, setPaginationInfo] = useState({});

  //modal
  const [downloadModalProps, setDownloadModalProps] = useState({});
  const [editModalProps, setEditModalProps] = useState({});
  const [editCommunityModalProps, setEditCommunityModalProps] = useState({});
  const [confirmationModalProps, setConfirmationModalProps] = useState({});
  const [communityUploadProps, setCommunityUploadProps] = useState({});

  //image accepted files
  const acceptedFiles = {
    'image/jpeg': [],
    'image/png': [],
    'image/webp': [],
    'image/heic': [],
    'image/jfif': [],
    'image/jpg': [],
  };

  //add event listened to be triggered whenever the played song ends
  useEffect(() => {
    if (selectedAudio) {
      selectedAudio.addEventListener('ended', () => {
        setSelectedCommunityId('');
        setPlaying(false);
      });
    }
    return () => {
      if (selectedAudio) {
        selectedAudio.pause();
        selectedAudio.removeEventListener('ended', () => {
          setSelectedCommunityId('');
          setPlaying(false);
        });
      }
    };
  }, [selectedAudio]);

  //when people click play button
  useEffect(() => {
    if (selectedAudio) {
      playing ? selectedAudio.play() : selectedAudio.pause();
    }
  }, [playing]);

  useEffect(() => {
    const debounced = _.debounce(() => initData(false), 500);
    if (userId) {
      debounced();
    }

    return () => {
      debounced.cancel();
    };
  }, [userId, alignment, query]);

  const initData = async scroll => {
    if (controllerRef.current) {
      controllerRef.current.abort();
    }
    controllerRef.current = new AbortController();
    const signal = controllerRef.current.signal;

    try {
      if (alignment === 'voice') {
        let trainingsChunk;
        if (scroll) {
          trainingsChunk = await fetchTrainingsChunk(chunk, chunkPage, query, signal);
        } else {
          trainingsChunk = await fetchTrainingsChunk(chunk, 0, query, signal);
        }
        let trainings = trainingsChunk.rows;

        const incompleteList = _.filter(trainings, { status: 'PENDING' });
        if (_.size(incompleteList) > 0) {
          await checkTrainingCompletion(userId, incompleteList);
          if (scroll) {
            trainingsChunk = await fetchTrainingsChunk(chunk, chunkPage, query, signal);
          } else {
            trainingsChunk = await fetchTrainingsChunk(chunk, 0, query, signal);
          }
          trainings = trainingsChunk.rows;
        }

        const cleanedData = trainings.map(({ id, label, status, createdAt, tier, shared, purchaseId }) => {
          return {
            id,
            label,
            createdAt: moment(createdAt).format('MM/DD/YY hh:mm A'),
            status,
            shared,
            tag: tier === 'FREE' && !purchaseId ? `[${t('historyTab.freeVersionTag')}]` : '',
          };
        });

        if (scroll) {
          setData([...data, ...cleanedData]);
          setChunkPage(prevChunkPage => prevChunkPage + 1);
        } else {
          setData(cleanedData);
          setChunkPage(1);
        }
        setLength(trainingsChunk.count);
      } else if (alignment === 'music') {
        let inferencesChunk;
        if (scroll) {
          inferencesChunk = await fetchInferencesChunk(chunk, chunkPage, query, signal);
        } else {
          inferencesChunk = await fetchInferencesChunk(chunk, 0, query, signal);
        }
        let inferences = inferencesChunk.rows;

        const incompleteList = _.filter(inferences, { status: 'PENDING' });
        if (_.size(incompleteList) > 0) {
          await checkInferenceCompletion(userId, incompleteList);
          if (scroll) {
            inferencesChunk = await fetchInferencesChunk(chunk, chunkPage, query, signal);
          } else {
            inferencesChunk = await fetchInferencesChunk(chunk, 0, query, signal);
          }
          inferences = inferencesChunk.rows;
        }
        const cleanedData = inferences.map(
          ({
            pitch,
            voiceId,
            id,
            label,
            status,
            createdAt,
            needMdx,
            outputFormat,
            tier,
            deletedAt,
            sourceLabel,
            purchaseId,
            voice,
            watermark,
          }) => {
            return {
              pitch,
              trainingId: voiceId,
              id,
              label,
              createdAt: moment(createdAt).format('MM/DD/YY hh:mm A'),
              status,
              needMdx,
              outputFormat,
              tag: tier === 'FREE' && (watermark || watermark === null) ? `[${t('historyTab.freePrefix')}]` : '',
              deletedAt,
              sourceLabel,
              trainingLabel: voice ? voice.label : 'AI voice not found',
            };
          }
        );

        if (scroll) {
          setData([...data, ...cleanedData]);
          setChunkPage(prevChunkPage => prevChunkPage + 1);
        } else {
          setData(cleanedData);
          setChunkPage(1);
        }
        setLength(inferencesChunk.count);
      } else if (alignment === 'community') {
        const communityItems = await fetchPagedUserCommunityItems(1, chunk, query, null, 'recent', userId);
        setData(communityItems.rows);
        setFilteredData(communityItems.rows);
        setPaginationInfo(communityItems.pagination);
      } else {
        //vocalExtract
        let vocalExtractsChunk;
        if (scroll) {
          vocalExtractsChunk = await fetchVocalExtractsChunk(chunk, chunkPage, query, signal);
        } else {
          vocalExtractsChunk = await fetchVocalExtractsChunk(chunk, 0, query, signal);
        }
        let vocalExtracts = vocalExtractsChunk.rows;

        const incompleteList = _.filter(vocalExtracts, {
          status: 'PENDING',
        });
        if (_.size(incompleteList) > 0) {
          await checkVocalExtractCompletion(userId, incompleteList);

          if (scroll) {
            vocalExtractsChunk = await fetchVocalExtractsChunk(chunk, chunkPage, query, signal);
          } else {
            vocalExtractsChunk = await fetchVocalExtractsChunk(chunk, 0, query, signal);
          }
          vocalExtracts = vocalExtractsChunk.rows;
        }
        const cleanedData = vocalExtracts.map(({ id, label, status, createdAt, outputFormat }) => {
          return {
            id,
            label,
            createdAt: moment(createdAt).format('MM/DD/YY hh:mm A'),
            status,
            outputFormat,
            tag: '',
          };
        });

        if (scroll) {
          setData([...data, ...cleanedData]);
          setChunkPage(prevChunkPage => prevChunkPage + 1);
        } else {
          setData(cleanedData);
          setChunkPage(1);
        }
        setLength(vocalExtractsChunk.count);
      }
    } catch (err) {
      if (err.name != 'CanceledError') {
        dispatch(appActions.openMessageModal(t('historyTab.modal.error'), false, true));
      }
    }
  };

  const fetchMoreData = async () => {
    try {
      //setPage(page + 1);

      const communityItems = await fetchPagedUserCommunityItems(
        paginationInfo.next,
        chunk,
        query,
        null,
        'recent',
        userId
      );
      setData([...data, ...communityItems.rows]);
      setFilteredData([...data, ...communityItems.rows]);
      setPaginationInfo(communityItems.pagination);
    } catch (err) {
      if (err.name != 'CanceledError') {
        dispatch(appActions.openMessageModal(t('historyTab.modal.error'), false, true));
      }
    }
  };

  const searchItems = searchValue => {
    setCommunityQuery(searchValue);
    setFilteredData(
      data.filter(item => {
        return Object.values(item).join('').toLowerCase().includes(communityQuery.toLowerCase());
      })
    );
  };

  const getBlobFromS3Url = async url => {
    const res = await fetch(url);
    return res.blob();
  };

  const handleShare = async id => {
    setCommunityUploadProps({
      open: true,
      selectedModelId: id,
      onCommunityModalClose: () => {
        setCommunityUploadProps({ open: false });
      },
    });
  };

  const handleCommunityEdit = async (id, label, anonymous) => {
    setEditCommunityModalProps({
      open: true,
      label,
      anonymous,
      onEdit: async (changedLabel, changedAnonymous, changedImage) => {
        setEditCommunityModalProps({ open: true, loading: true });
        const updatedCommunityItem = await updateCommunityItemById(
          id,
          { label: changedLabel, anonymous: changedAnonymous },
          changedImage
        );
        console.log(updatedCommunityItem);

        const copiedFilteredData = filteredData.map(obj => {
          if (obj.id === updatedCommunityItem.id) {
            let newObj = { ...obj };
            newObj.image = updatedCommunityItem.image; //URL.createObjectURL(file);
            newObj.label = updatedCommunityItem.label;
            newObj.username = updatedCommunityItem.username;
            newObj.anonymous = updatedCommunityItem.anonymous;
            return newObj;
            //return { ...obj, likedNumber: obj.likedNumber - 1 };
          }
          return obj;
        });

        const copiedData = data.map(obj => {
          if (obj.id === id) {
            let newObj = { ...obj };
            newObj.image = updatedCommunityItem.image; //URL.createObjectURL(file);
            newObj.label = updatedCommunityItem.label;
            newObj.username = updatedCommunityItem.username;
            newObj.anonymous = updatedCommunityItem.anonymous;
            return newObj;
          }
          return obj;
        });
        setData(copiedData);
        setFilteredData(copiedFilteredData);
        setEditCommunityModalProps({ open: false });
      },
      onEditCommunityModalClose: () => {
        setEditCommunityModalProps({ open: false });
      },
    });
  };

  const handleDelete = async (id, type, alignment) => {
    setConfirmationModalProps({
      open: true,
      message:
        alignment === 'voice' && type !== 'FAILED'
          ? t('historyTab.confirmModelDelete')
          : (alignment === 'music' || alignment === 'community') && type !== 'FAILED'
            ? t('historyTab.confirmInferenceDelete')
            : t('historyTab.confirmVocalExtractDelete'),
      onConfirmation: async response => {
        if (response === true) {
          setConfirmationModalProps({ open: true, loading: true });
          if (alignment === 'voice') {
            await updateTrainingById(id, { deletedAt: moment() });
          } else if (alignment === 'music') {
            await updateInferenceById(id, { deletedAt: moment() });
          } else if (alignment === 'community') {
            await deleteCommunityItemById(id, { deletedAt: moment() });
          } else {
            // alignment === "vocalExtract"
            await updateVocalExtractById(id, { deletedAt: moment() });
          }
          if (alignment !== 'community') {
            let copiedData = [...data];
            copiedData = copiedData.filter(obj => obj.id !== id);
            setData(copiedData);
          } else {
            //let copiedCommunityItems = [...communityItems];
            const copiedCommunityItems = data.filter(item => {
              return item.id !== id;
            });
            setData(copiedCommunityItems);
            setFilteredData(copiedCommunityItems);
          }
        }
        setConfirmationModalProps({ open: false });
      },
      onConfirmationModalClose: () => setConfirmationModalProps({ open: false }),
    });
  };

  const handleEdit = async (id, label, alignment) => {
    setEditModalProps({
      open: true,
      label,
      onEdit: async changedLabel => {
        setEditModalProps({ open: true, loading: true });
        if (alignment === 'voice') {
          await updateTrainingById(id, { label: changedLabel });
        } else if (alignment === 'music') {
          await updateInferenceById(id, { label: changedLabel });
        } else if (alignment === 'community') {
          await updateCommunityItemById(id, { label: changedLabel });
        } else {
          // alignment === "vocalExtract"
          await updateVocalExtractById(id, { label: changedLabel });
        }
        let copiedData = data.map(obj => {
          if (obj.id === id) {
            return { ...obj, label: changedLabel };
          }
          return obj;
        });
        setData(copiedData);

        if (alignment === 'community') {
          setData(copiedData);
          let copiedFilteredData = filteredData.map(obj => {
            if (obj.id === id) {
              return { ...obj, label: changedLabel };
            }
            return obj;
          });
          setData(copiedData);
          setFilteredData(copiedFilteredData);
        }
        setEditModalProps({ open: false });
      },
      onEditModalClose: () => {
        setEditModalProps({ open: false });
      },
    });
  };

  const handlePlay = async (communityItemId, label) => {
    if (selectedCommunityId === communityItemId) {
      setPlaying(true);
    } else {
      setPlaying(false);
      const sampleVoiceUrl = await getCommunitySampleSignedUrl(communityItemId);
      const sampleVoiceBlob = await getBlobFromS3Url(sampleVoiceUrl);
      if (sampleVoiceBlob.size > 2000) {
        const audio = await new Audio(URL.createObjectURL(sampleVoiceBlob.slice(0, sampleVoiceBlob.size, `audio/mp3`)));
        setSelectedAudio(audio);
        setPlaying(true);
        setSelectedCommunityId(communityItemId);
      }
    }
  };

  const handlePlay2 = _.debounce(handlePlay, 500, { leading: true });

  const handlePause = async (communityItemId, label) => {
    setPlaying(false);
  };

  const handleLike = async communityItemId => {
    likeCommunityItem(communityItemId);
    const copiedData = data.map(obj => {
      if (obj.id === communityItemId) {
        return {
          ...obj,
          likedNumber: Number(obj.likedNumber) + 1,
          liked: true,
        };
      }
      return obj;
    });
    const copiedFilteredData = filteredData.map(obj => {
      if (obj.id === communityItemId) {
        return {
          ...obj,
          likedNumber: Number(obj.likedNumber) + 1,
          liked: true,
        };
      }
      return obj;
    });

    setData(copiedData);
    setFilteredData(copiedFilteredData);
  };

  const handleDislike = async communityItemId => {
    dislikeCommunityItem(communityItemId);
    const copiedData = data.map(obj => {
      if (obj.id === communityItemId) {
        let newObj = { ...obj };
        newObj.likedNumber = Number(obj.likedNumber) - 1;
        newObj.liked = false;
        return newObj;
      }
      return obj;
    });
    const copiedFilteredData = filteredData.map(obj => {
      if (obj.id === communityItemId) {
        let newObj = { ...obj };
        newObj.likedNumber = Number(obj.likedNumber) - 1;
        newObj.liked = false;
        return newObj;
        //return { ...obj, likedNumber: obj.likedNumber - 1 };
      }
      return obj;
    });

    setData(copiedData);
    setFilteredData(copiedFilteredData);
  };

  const handleDownload = async (id, needMdx, label, alignment, outputFormat, tag) => {
    setDownloadModalProps({
      open: true,
      id,
      needMdx,
      label,
      alignment,
      outputFormat,
      tag,
      onDownloadClose: () => {
        setDownloadModalProps({ open: false });
      },
    });
  };

  const classes = useStyles();
  return (
    <>
      <CommunityUploadModal {...communityUploadProps} />
      <DownloadModal {...downloadModalProps} />
      <ConfirmationModal {...confirmationModalProps} />
      <EditLabelModal {...editModalProps} />
      <EditCommunityModal {...editCommunityModalProps} />
      <div className={classes.pageTitle}>
        {t('historyTab.title')}
        <HtmlTooltip
          title={
            <div className={classes.tooltipContainer}>
              <div className={classes.tooltipTextContainer}>
                <div className={`${classes.tooltipText} ${classes.tooltipTextBullet}`}>&#x2022;</div>
                <div className={classes.tooltipText}>{t('historyTab.tooltip.contents.0')}</div>
              </div>
              <div className={classes.tooltipTextContainer}>
                <div className={`${classes.tooltipText} ${classes.tooltipTextBullet}`}>&#x2022;</div>
                <div className={classes.tooltipText}>{t('historyTab.tooltip.contents.1')}</div>
              </div>
              <div className={classes.tooltipTextContainer}>
                <div className={`${classes.tooltipText} ${classes.tooltipTextBullet}`}>&#x2022;</div>
                <div className={classes.tooltipText}>{t('historyTab.tooltip.contents.2')}</div>
              </div>
            </div>
          }
        >
          <img className={classes.tooltipImg} src={tooltipImg} alt="tooltip-img" />
        </HtmlTooltip>
      </div>

      <div className={classes.separater} />
      <div className={classes.inputSourceBarContainer}>
        <ButtonGroup className={classes.inputSourceButtonContainer}>
          <Button
            className={`${classes.inputSourceButton} ${alignment == 'music' ? classes.selected : ''}`}
            onClick={() => {
              if (alignment != 'music') {
                setAlignment('music');
                setData([]);
                setQuery('');
              }
            }}
            style={{ fontSize: 'inherit' }}
          >
            {t('historyTab.tabs.inference.title')}
          </Button>
          <Button
            className={`${classes.inputSourceButton} ${alignment == 'voice' ? classes.selected : ''}`}
            onClick={() => {
              if (alignment != 'voice') {
                setAlignment('voice');
                setData([]);
                setQuery('');
              }
            }}
            style={{ fontSize: 'inherit' }}
          >
            {t('historyTab.tabs.training.title')}
          </Button>
          <Button
            className={`${classes.inputSourceButton} ${alignment == 'vocalExtract' ? classes.selected : ''}`}
            onClick={() => {
              if (alignment != 'vocalExtract') {
                setAlignment('vocalExtract');
                setData([]);
                setQuery('');
              }
            }}
            style={{ fontSize: 'inherit' }}
          >
            {t('historyTab.tabs.vocalExtract.title')}
          </Button>
          <Button
            className={`${classes.inputSourceButton} ${alignment == 'community' ? classes.selected : ''}`}
            onClick={() => {
              if (alignment != 'community') {
                setAlignment('community');
                setData([]);
                setQuery('');
                setChunkPage(0);
              }
            }}
            style={{ fontSize: 'inherit' }}
          >
            {t('historyTab.tabs.community.title')}
          </Button>
        </ButtonGroup>
      </div>

      <input
        className={classes.searchbar}
        placeholder={t('historyTab.searchbar')}
        value={query}
        onChange={query => setQuery(query.target.value)}
      />

      {/*alignment === "community" && (
        <input
          className={classes.searchbar}
          placeholder={t('historyTab.searchbar')}
          value={communityQuery}
          onChange={communityQuery => searchItems(communityQuery.target.value)}
        />
      )*/}
      {alignment !== 'community' && (
        <InfiniteScroll
          className={classes.infiniteScroll}
          dataLength={data.length}
          next={() => initData(true)}
          hasMore={data.length < length}
          loader={<div className={classes.pagination}>{t('historyTab.pagination.loader')}</div>}
        >
          {data
            .sort((a, b) => moment(b.createdAt).diff(moment(a.createdAt)))
            .map(
              ({
                pitch,
                id,
                label,
                createdAt,
                status,
                needMdx,
                tag,
                outputFormat,
                sourceLabel,
                trainingLabel,
                shared,
              }) => {
                return (
                  <div className={classes.card}>
                    <div className={classes.cardLabel}>
                      {label} {tag}
                      <EditIcon onClick={() => handleEdit(id, label, alignment)} className={classes.editIcon} />
                    </div>
                    <div className={classes.cardTimestamp}>{createdAt}</div>
                    {status === 'FAILED' && <div className={classes.cardStatus}>{t('historyTab.failed')}</div>}
                    {status === 'PENDING' && <div className={classes.cardStatus}>{t('historyTab.inProgress')}</div>}
                    {(alignment == 'music' || alignment == 'vocalExtract') && status === 'COMPLETE' && (
                      <div className={classes.cardAction}>
                        <DownloadIcon
                          onClick={() => {
                            handleDownload(id, needMdx, label, alignment, outputFormat, tag);
                          }}
                        />
                      </div>
                    )}
                    {alignment == 'music' && status === 'COMPLETE' && (
                      <div className={classes.infoIconAction}>
                        <HtmlInfoIcon
                          title={
                            <div className={classes.infoIconContainer}>
                              <div className={classes.infoIconTextContainer}>
                                <div className={classes.infoIconTextLine}>
                                  <div className={classes.infoIconTextBold}>{t('historyTab.infoIcon.source')}</div>
                                  <div className={classes.infoIconText}>: {sourceLabel}</div>
                                </div>
                                <div className={classes.infoIconTextLine}>
                                  <div className={classes.infoIconTextBold}>{t('historyTab.infoIcon.voice')}</div>
                                  <div className={classes.infoIconText}>: {trainingLabel}</div>
                                </div>
                                <div className={classes.infoIconTextLine}>
                                  <div className={classes.infoIconTextBold}>{t('historyTab.infoIcon.pitch')}</div>
                                  <div className={classes.infoIconText}>: {pitch}</div>
                                </div>
                              </div>
                            </div>
                          }
                        >
                          <img className={classes.infoIconImg} src={infoIconImg} alt="tooltip-img" />
                        </HtmlInfoIcon>
                      </div>
                    )}
                    {alignment == 'voice' && status === 'COMPLETE' && !shared && (
                      <div className={classes.cardAction}>
                        <ShareOutlinedIcon
                          disabled={shared}
                          onClick={() => {
                            handleShare(id);
                          }}
                        />
                      </div>
                    )}
                    {(status === 'COMPLETE' || status === 'FAILED') && (
                      <div className={classes.cardAction}>
                        <DeleteIcon onClick={() => handleDelete(id, status, alignment)} />
                      </div>
                    )}
                  </div>
                );
              }
            )}
        </InfiniteScroll>
      )}
      {alignment === 'community' && (
        <InfiniteScroll
          className={classes.infiniteScroll}
          dataLength={data.length}
          next={fetchMoreData}
          hasMore={paginationInfo.has_next}
          loader={<div className={classes.pagination}>{t('historyTab.pagination.loader')}</div>}
        >
          {data
            .sort((a, b) => moment(b.createdAt).diff(moment(a.createdAt)))
            .map(
              ({
                id,
                gender,
                label,
                username,
                createdAt,
                status,
                trainingLabel,
                image,
                likedNumber,
                numberUsed,
                anonymous,
                liked,
              }) => {
                return (
                  <Grid className={classes.communityCard} item container key={id}>
                    <Grid item xs={'auto'} sm={'auto'} md={'auto'}>
                      <div
                        container
                        className={classes.coverImg}
                        style={{
                          backgroundImage: `url(${image})`,
                          backgroundSize: 'contain',
                        }}
                      ></div>
                    </Grid>
                    <Grid
                      container
                      className={classes.communityCardSecondColumn}
                      //style={{ border: "1px solid #fafafa" }}
                      item
                      xs
                      sm
                      md
                    >
                      <Grid container item>
                        <Grid container item xs={9} sm={10} md={10}>
                          <div className={classes.communityCardLabel}>{label} </div>
                        </Grid>
                        <Grid container item className={classes.playButtonGrid} xs={3} sm={2} md={2}>
                          {playing && id === selectedCommunityId ? (
                            <PauseCircleOutlineIcon
                              className={classes.playButtonImg}
                              onClick={event => {
                                event.stopPropagation();
                                handlePause(id, label);
                              }}
                            />
                          ) : (
                            <PlayCircleOutlineIcon
                              className={classes.playButtonImg}
                              onClick={event => {
                                event.stopPropagation();
                                handlePlay2(id, label);
                              }}
                            />
                          )}
                          <EditIcon
                            onClick={() => handleCommunityEdit(id, label, anonymous)}
                            className={classes.editIcon}
                          />
                          <DeleteIcon
                            onClick={() => handleDelete(id, status, alignment)}
                            className={classes.communityDeleteButtonImg}
                          />
                        </Grid>
                      </Grid>
                      <Grid container item>
                        <div className={classes.communityCardUsername}>{username}</div>
                      </Grid>
                      <Grid
                        container
                        item
                        style={{
                          maxHeight: '2rem',
                        }}
                      >
                        <Grid
                          className={classes.communityCreationsGrid}
                          container
                          item
                          xs={4}
                          sm={4}
                          md={4}
                          style={{
                            maxHeight: '1.1rem',
                          }}
                        >
                          <div className={classes.communityNumberPlayed}>{numberUsed}</div>
                          <div className={classes.communityCreations}>{'creations'}</div>
                        </Grid>
                        <Grid
                          container
                          item
                          xs={5}
                          sm={5}
                          md={5}
                          style={{
                            alignItems: 'flex-end',
                            maxHeight: '1.1rem',
                          }}
                        >
                          <div className={classes.communityCardTimestamp}>
                            <ReactTimeAgo date={createdAt} locale="en-US" />
                          </div>
                        </Grid>
                        <Grid
                          container
                          item
                          xs={3}
                          sm={3}
                          md={3}
                          className={classes.communityCardLikes}
                          style={{
                            maxHeight: '2rem',
                          }}
                        >
                          {!liked && (
                            <FavoriteBorderRoundedIcon
                              className={classes.favoriteImg}
                              onClick={() => handleLike(id)}
                              fontSize="inherit"
                            />
                          )}

                          {liked && (
                            <FavoriteIcon
                              className={classes.favoriteFullImg}
                              onClick={() => handleDislike(id)}
                              fontSize="inherit"
                            />
                          )}
                          <div className={classes.numberLikes}>{likedNumber}</div>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                );
              }
            )}
        </InfiniteScroll>
      )}
    </>
  );
}
export default MyVoicePage;
